import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, keyframes } from '@emotion/react';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { Fragment, useCallback, useId } from 'react';
import { BOLD_FONT_FAMILY } from '../../constants/constants';
import { LIGHT_BLUE_COLOR, LIGHT_GREEN_COLOR, LIGHT_YELLOW_COLOR, YELLOW_COLOR } from '../../styles/colors';
import { getFrequencyString } from '../../types/autoMttTypes';
import { CURRENCY_NAMES } from '../../types/sharedTypes';
import { MarkdownRenderer } from '../markdownRenderer';
import { ValueDisplay } from '../valueDisplay';
const HEADER_CSS = css `
  display: flex;
  flex-flow: row wrap;
  gap: 0 5px;
  align-items: center;
`;
const DETAILS_CONTAINER_CSS = css `
  padding-left: 15px;
  border-left: 2px solid ${LIGHT_BLUE_COLOR};
  border-bottom-left-radius: 10px;
`;
const TOURNEY_CONTAINER_CSS = css `
  padding-top: 10px;
  padding-bottom: 25px;
`;
const NAME_CSS = css `
  flex-grow: 1;
  line-height: 1;
  color: ${LIGHT_BLUE_COLOR};
  font-size: 125%;
  font-family: ${BOLD_FONT_FAMILY};
  filter: drop-shadow(2px 2px 0 rgba(0, 0, 0, 0.25));
`;
const DETAILS_CSS = css `
  display: flex;
  flex-direction: column;
  font-size: 85%;
  line-height: 1.5em;
`;
const BLINKER_KEYFRAMES = keyframes `
  0% {
    opacity: 0.25;
  }
  50% {
    opacity: 0.75;
  }
  100% {
    opacity: 0.25;
  }
`;
const BLINKING_CLOCK_CSS = css `
  animation: ${BLINKER_KEYFRAMES} ease-in 1.5s infinite;
`;
const REQUIREMENT_DETAIL_CSS = css `
  color: ${LIGHT_YELLOW_COLOR};
`;
const WIN_DETAIL_CSS = css `
  color: ${LIGHT_GREEN_COLOR};
`;
const NEXT_RUN_ROW_CSS = css `
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 0 5px;
`;
const FREQUENCY_DETAIL_CSS = css `
  color: #aaa;
`;
const HAND_TYPES_LABELS = {
    any: '',
    regular: 'non-tourney',
    tourney: 'tourney'
};
const MARKDOWN_CONTAINER_CSS = css `
  padding: 10px 0;
  font-size: 120%;
`;
const COUNTDOWN_CONTAINER_CSS = css `
  display: flex;
  flex-direction: row;
  gap: 5px;
  white-space: nowrap;
  align-items: center;
`;
function getRecentHandsRequirementStr({ numHands, numDays, handType }) {
    const handTypeLabel = HAND_TYPES_LABELS[handType];
    return `${numHands.toLocaleString()} ${handTypeLabel ? `(${handTypeLabel}) ` : ''}in past ${numDays > 1 ? `${numDays} ` : ''}day${numDays > 1 ? 's' : ''}`;
}
function getTourneyDetailNodes(tourneyDetails, isSingleView) {
    const { addExtraAmountToPrizePool, buyIn, jacksRequired, currency, numTicketsRequired, ticketRequired, ticketToWin, isPowerPlayerOnly, extraChipsToWin, extraJacksToWin, frequencyCount, frequencyUnitType, recentHandsRequirement, infoMarkdown, powerPlayerBonusStartingChipsPercent } = tourneyDetails;
    const details = [];
    if (infoMarkdown) {
        details.push(_jsx("div", { css: MARKDOWN_CONTAINER_CSS, children: _jsx(MarkdownRenderer, { markdown: infoMarkdown }) }));
    }
    // requirements:
    details.push(_jsxs("span", { css: REQUIREMENT_DETAIL_CSS, children: ["Buy-in: ", _jsx(ValueDisplay, { type: currency, isLabelFirst: false, value: buyIn, label: CURRENCY_NAMES[currency] })] }));
    if (jacksRequired) {
        details.push(_jsxs("span", { css: REQUIREMENT_DETAIL_CSS, children: ["Costs Extra:", ' ', _jsx(ValueDisplay, { type: 'jacks', isLabelFirst: false, value: jacksRequired, label: CURRENCY_NAMES.jacks })] }));
    }
    if (recentHandsRequirement) {
        details.push(_jsxs("span", { css: REQUIREMENT_DETAIL_CSS, children: ["Required Hands: ", getRecentHandsRequirementStr(recentHandsRequirement)] }));
    }
    if (numTicketsRequired !== undefined && numTicketsRequired > 0 && ticketRequired) {
        details.push(_jsxs("span", { css: REQUIREMENT_DETAIL_CSS, children: ["Required Ticket", numTicketsRequired > 1 ? 's' : '', ": ", numTicketsRequired > 1 ? `${numTicketsRequired}x ` : '', ' ', ticketRequired] }));
    }
    if (isPowerPlayerOnly) {
        details.push(_jsx("span", { css: REQUIREMENT_DETAIL_CSS, children: "Power Players Only" }));
    }
    // bonuses to win:
    const bonusParts = [];
    if (extraChipsToWin) {
        bonusParts.push(_jsx(ValueDisplay, { type: 'chips', isLabelFirst: false, value: extraChipsToWin, label: CURRENCY_NAMES.chips }));
    }
    if (extraJacksToWin) {
        bonusParts.push(_jsx(ValueDisplay, { type: 'jacks', isLabelFirst: false, value: extraJacksToWin, label: CURRENCY_NAMES.jacks }));
    }
    if (bonusParts.length) {
        const bonusPartsLabel = addExtraAmountToPrizePool ? 'Bonus to Prize Pool' : 'Bonus to Winner';
        details.push(_jsxs("span", { css: WIN_DETAIL_CSS, children: [bonusPartsLabel, ":", ' ', bonusParts.map((bonusPart, i) => (_jsxs(Fragment, { children: [i > 0 ? ' + ' : '', bonusPart] }, i)))] }));
    }
    if (powerPlayerBonusStartingChipsPercent) {
        details.push(_jsxs("span", { children: ["Power Player Starting Chips: ", powerPlayerBonusStartingChipsPercent > 0 ? '+' : '', powerPlayerBonusStartingChipsPercent, "%"] }));
    }
    if (ticketToWin) {
        details.push(_jsxs("span", { css: WIN_DETAIL_CSS, children: ["Wins Ticket: ", ticketToWin] }));
    }
    // frequency:
    if (frequencyCount !== undefined && frequencyUnitType !== undefined && frequencyUnitType !== 'months') {
        // If the tourney runs at most once a month, there may be multiple MTTs that are at staggered times.
        // (For example, a "monthly" tourney that has 3 series that each have a frequency of 3 months, for the
        // purposes of staggering times so anyone in a given time zone will eventually have a convenient time.)
        // In these cases it might be confusing to show the frequency.
        details.push(_jsxs("span", { css: FREQUENCY_DETAIL_CSS, children: ["Runs Every: ", getFrequencyString(frequencyCount, frequencyUnitType), isSingleView ? '' : '*'] }));
    }
    return details;
}
export const TourneyDetailsDisplay = ({ tourneyDetails, isSingleView }) => {
    const elementId = useId();
    const { name, secsUntilStart, timeUntilStartStr } = tourneyDetails;
    const renderCountdown = useCallback(() => {
        if (secsUntilStart === undefined || timeUntilStartStr === undefined) {
            return null;
        }
        const isStartingVerySoon = secsUntilStart <= 5 * 60;
        const startsInStr = isStartingVerySoon ? 'Very Soon' : `in ${timeUntilStartStr}`;
        return (_jsx("div", { css: NEXT_RUN_ROW_CSS, children: _jsxs("span", { css: COUNTDOWN_CONTAINER_CSS, children: [_jsx(ScheduleRoundedIcon, { css: BLINKING_CLOCK_CSS }), _jsx("span", { style: isStartingVerySoon ? { color: YELLOW_COLOR } : undefined, children: startsInStr })] }) }));
    }, [secsUntilStart, timeUntilStartStr]);
    const contents = (_jsx("div", { css: DETAILS_CSS, children: getTourneyDetailNodes(tourneyDetails, isSingleView).map((detail, j) => (_jsx("span", { children: detail }, j))) }));
    if (isSingleView) {
        return contents;
    }
    return (_jsxs("div", { "aria-labelledby": elementId, css: TOURNEY_CONTAINER_CSS, children: [_jsxs("div", { css: HEADER_CSS, children: [_jsx("div", { id: elementId, css: NAME_CSS, children: name }), renderCountdown()] }), _jsx("div", { css: DETAILS_CONTAINER_CSS, children: contents })] }));
};
